import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Undo2, Archive, Grid3x3 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { UserName } from "@/components/UserName";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// SET UP EAST APRON — ONE TAP, STARTED BY HIM (his call, 21 Oct). The preview is every code it will make and the
// zone it archives; nothing happens until he presses Go, and Undo puts it all back.
const HOLD_WORDS = { parts: "part", containers: "container", trucks: "truck", people: "person", transfers: "transfer" };

const Codes = ({ rack }) => {
  const byBay = {};
  rack.codes.forEach((c) => { const b = c.split("-B")[1].split("-")[0]; (byBay[b] = byBay[b] || []).push(c); });
  return (
    <div data-testid="east-apron-codes" className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2 space-y-1">
      {Object.entries(byBay).map(([b, codes]) => (
        <div key={b} className="flex flex-wrap items-center gap-1">
          <span className="w-12 shrink-0 font-mono text-[10px] uppercase tracking-wider text-slate-400">Bay {b}</span>
          {codes.map((c) => <span key={c} data-testid={`east-apron-code-${c}`} className="rounded bg-white border border-slate-200 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">{c}</span>)}
        </div>
      ))}
    </div>
  );
};

const ArchiveLine = ({ a }) => {
  const held = Object.entries(a.holds || {}).filter(([, n]) => n > 0);
  return (
    <div data-testid="east-apron-archive" className={`rounded-lg border p-3 text-sm ${a.can_archive ? "border-slate-200 bg-white" : "border-red-200 bg-red-50"}`}>
      <div className="flex items-center gap-2 font-semibold text-slate-800"><Archive className="w-4 h-4 text-slate-500" />Archives {a.name} <span className="font-mono">{a.code}</span></div>
      <div className="text-[12px] text-slate-600 mt-1">{a.places} place{a.places === 1 ? "" : "s"} go with it. Archived, never deleted — Undo brings every one back under the same code.</div>
      {a.printed > 0 && <div data-testid="east-apron-printed-warning" className="text-[12px] text-amber-800 mt-1">{a.printed} of its labels are already printed — take those stickers down once this is done.</div>}
      {!a.can_archive && <div data-testid="east-apron-blocked" className="text-[12px] font-semibold text-[#B01E28] mt-1">Still on it: {held.map(([k, n]) => `${n} ${HOLD_WORDS[k] || k}${n === 1 ? "" : "s"}`).join(", ")}. Move them first — Go stays off until it is empty.</div>}
    </div>
  );
};

export function EastApronSetup({ onChanged }) {
  const [plan, setPlan] = useState(null);
  const [open, setOpen] = useState(false);
  const [undoOpen, setUndoOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/locations/east-apron-setup").then(({ data }) => setPlan(data)).catch(() => setPlan(null));
  useEffect(() => { load(); }, []);

  const run = async (path, done) => {
    setBusy(true);
    try {
      await api.post(`/locations/east-apron-setup/${path}`);
      toast.success(done);
      setOpen(false); setUndoOpen(false);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nothing was changed."); }
    setBusy(false);
  };

  if (!plan?.available || (plan.state === "done" && !plan.show)) return null;
  if (plan.state === "done") {
    return (
      <div data-testid="east-apron-done" className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-sm text-emerald-900">
        <MapPin className="w-4 h-4 shrink-0" />
        <span>East Apron <b className="font-mono">{plan.zone_code}</b> was set up by <UserName email={plan.by} /> on {new Date(plan.at).toLocaleDateString()}{plan.archived_code ? <> and <span className="font-mono">{plan.archived_code}</span> was archived</> : null}. Print its labels from the rack in the list below.</span>
        <Button data-testid="east-apron-undo-open" variant="ghost" size="sm" onClick={() => setUndoOpen(true)} className="ml-auto h-8 text-xs text-[#B01E28] underline underline-offset-2"><Undo2 className="w-3.5 h-3.5 mr-1" />Undo</Button>
        <Dialog open={undoOpen} onOpenChange={setUndoOpen}>
          <DialogContent data-testid="east-apron-undo-dialog" className="rounded-2xl sm:max-w-md">
            <DialogTitle>Undo the East Apron set-up?</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">{plan.zone_code} and its rack are archived — refused if anything has been put on them since — and {plan.archived_code || "the old zone"} comes back exactly as it was. Press Go again later and the same codes come back.</DialogDescription>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUndoOpen(false)}>Keep it</Button>
              <Button data-testid="east-apron-undo-confirm" disabled={busy} onClick={() => run("undo", "Undone — East Apron is archived and the old zone is back.")} className="bg-[#B01E28] hover:bg-[#8F1820] text-white">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Undo it"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  const { zone, rack, archive } = plan;
  return (
    <div data-testid="east-apron-setup" className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border-2 border-sky-200 bg-sky-50/60 px-4 py-3">
      <Grid3x3 className="w-5 h-5 text-sky-700 shrink-0" />
      <div className="min-w-0 flex-1 text-sm text-slate-700">
        <div className="font-display font-bold text-slate-900">Set up East Apron ({zone.code})</div>
        One tap makes the zone inside the Yard with its {rack.bays} × {rack.levels.length} pallet rack ({rack.count} codes){archive ? <> and archives <span className="font-mono">{archive.code}</span></> : null}. You see every code first.
      </div>
      <Button data-testid="east-apron-open" onClick={() => setOpen(true)} className="rounded-lg h-10 bg-sky-700 hover:bg-sky-800 text-white">See exactly what it makes</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="east-apron-dialog" className="rounded-2xl sm:max-w-xl">
          <DialogTitle>Set up East Apron</DialogTitle>
          <DialogDescription className="text-xs text-slate-500 -mt-1">Nothing changes until you press Go. Undo puts it all back.</DialogDescription>
          <div className="space-y-3 text-sm">
            <div data-testid="east-apron-zone" className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="font-semibold text-slate-800">Zone: {zone.name} <span className="font-mono">{zone.code}</span></div>
              <div className="text-[12px] text-slate-600">Inside {zone.inside}.</div>
            </div>
            <div data-testid="east-apron-rack" className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
              <div className="font-semibold text-slate-800">Pallet rack <span className="font-mono">{rack.code}</span> — {rack.bays} bays × {rack.levels.length} levels, {rack.count} codes</div>
              <div className="text-[12px] text-slate-600">On the warehouse pattern, {rack.shape_from}. Levels: {rack.levels.map((l) => `${l.level} = ${l.name.toLowerCase()}`).join(" · ")}.</div>
              <Codes rack={rack} />
            </div>
            {archive && <ArchiveLine a={archive} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="east-apron-go" disabled={busy || (archive && !archive.can_archive)}
              onClick={() => run("go", `East Apron is set up — ${zone.code} with ${rack.count} rack positions.`)}
              className="bg-[#E0781A] hover:bg-[#C76714] text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : plan.state === "undone" ? "Go — bring it back" : "Go — make it"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
